import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Stack } from '@mui/material'
import { PlusIcon } from 'lucide-react'

import { Modal, Button } from '@repo/ui'
import { UserBlock, Select, type SelectItem } from 'components'

import { ROUTES } from 'routes'
import { getErrorMessage } from 'utils'
import { getRoleDisplayName } from 'constants/roles'
import { useSnackbar } from 'contexts'
import { useWorkOSAuth, useReferrerState } from 'hooks'
import { useCompanyRoles, useUpdateMemberRole, CompanyRoleListItem } from 'hooks/api'

interface AssignRoleModalProps {
  /** The member row whose role is being assigned (CompanyUser). */
  user: any
  open: boolean
  onClose: () => void
  refetch?: () => void
}

/**
 * Assign a company role to a member. The member-role endpoint resolves the role
 * name against the global roles table and sets `users.role_id`, so only built-in
 * + functional (global) roles are assignable here — custom (company-scoped) roles
 * live in a separate table and aren't assignable yet. The "Create custom role"
 * link routes to the Roles page to create one. TODO(VERA-787): assign custom roles.
 */
export const AssignRoleModal = ({ user, open, onClose, refetch }: AssignRoleModalProps) => {
  const navigate = useNavigate()
  const { open: notification } = useSnackbar()
  const { user: workosUser } = useWorkOSAuth()
  const companyId = workosUser?.companyId || workosUser?.company?.id || ''

  const { data: roles = [], isLoading: isLoadingRoles } = useCompanyRoles()
  const assignableRoles = roles.filter((r: CompanyRoleListItem) => !r.isCustom)

  const currentRoleName: string = user?.role?.name ?? ''
  const [selectedRole, setSelectedRole] = useState<string>(currentRoleName)

  // Default the dropdown to the member's current role whenever the modal opens.
  useEffect(() => {
    if (open) setSelectedRole(user?.role?.name ?? '')
  }, [open, user])

  const { mutate: updateRole, isPending } = useUpdateMemberRole(
    { companyId, userId: user?.id ?? '' },
    {
      onSuccess: () => {
        onClose()
        refetch?.()
      },
      onError: (error: any) => {
        notification({ type: 'error', title: getErrorMessage(error) })
      },
    },
  )

  const roleOptions: SelectItem[] = assignableRoles.map((r: CompanyRoleListItem) => ({
    label: getRoleDisplayName(r),
    value: r.name,
  }))

  const handleAssign = () => {
    if (!selectedRole) return
    updateRole({ roleName: selectedRole })
  }

  // Carries the referrer (§42) so Roles & permissions can offer the way back to
  // wherever this modal was opened from — this modal is reachable from several
  // surfaces (a user row, a user detail page), so the derived label is what makes
  // the return trip correct rather than a hardcoded guess.
  const referrer = useReferrerState()

  const handleCreateCustomRole = () => {
    onClose()
    navigate(`${ROUTES.SETTINGS_COMPANY_ROLES}?create=1`, { state: referrer })
  }

  const unchanged = selectedRole === currentRoleName

  return (
    <Modal
      title="Assign role"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedRole || unchanged || isPending}
            loading={isPending}
          >
            Assign
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {/* Member card — bordered, matching the other user modals (Archive/Delete). */}
        <Box
          sx={(theme) => ({
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <UserBlock user={user} />
        </Box>

        {/* Available roles — body2 label to match the other field titles. */}
        <Select
          label="Role"
          labelVariant="body2"
          options={roleOptions}
          value={selectedRole}
          placeholder="Select a role"
          disabled={isLoadingRoles || roleOptions.length === 0}
          onChange={(value) => setSelectedRole(value as string)}
        />

        {/* Create a custom role on the Roles page first, then come back to assign it. */}
        <Button
          variant="text"
          onClick={handleCreateCustomRole}
          startIcon={<PlusIcon size={16} strokeWidth={1.5} />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Create custom role
        </Button>
      </Stack>
    </Modal>
  )
}
